import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@egaranti/components";

import { useTranslation } from "react-i18next";

import { CalendarIcon, DocumentIcon } from "./icons";

import TrashIcon from "@/assets/icons/trash.svg?react";

import { ExternalLink, FileText } from "lucide-react";

const ProductCard = ({
  product,
  onDelete,
  onEditWarrantyDate,
  onUploadBill,
}) => {
  const { t } = useTranslation();

  const getSourceInfo = (sourceType) => {
    switch (sourceType) {
      case "EXTERNAL_INTEGRATION":
        return {
          text: t("clientsPage.externalIntegration", "Harici Entegrasyon"),
          color: "yellow",
        };
      case "PANEL":
        return { text: t("clientsPage.panel", "Panel"), color: "blue" };
      case "SERVICE_RECORD":
        return {
          text: t("clientsPage.serviceRecord", "Servis Kayıt"),
          color: "blue",
        };
      case "API_INTEGRATION":
        return {
          text: t("clientsPage.apiIntegration", "API Entegrasyon"),
          color: "yellow",
        };
      case "PARTNER":
        return { text: t("clientsPage.partner", "Partner"), color: "yellow" };
      case "PRODUCT_REGISTER":
        return {
          text: t("clientsPage.productRegister", "Ürün Kayıt"),
          color: "green",
        };
      default:
        return { text: t("clientsPage.panel", "Panel"), color: "blue" };
    }
  };

  return (
    <div className="rounded-lg border bg-white p-4 transition-all hover:border-gray-300">
      {/*
        EPS-5120 (QA turu) — uzun urun adi aksiyon butonlarini kartin disina
        itiyordu. Flex cocuklarinin ortulu `min-width: auto` tabani yuzunden ad
        kutusu icerigi kadar genisliyor, butonlar da tasiyordu: Beste'nin
        ekraninda `QA_AUTOMATION_TEST_DELETE_ME...` adli urunun takvim/belge/cop
        butonlari ekranin disinda kaliyordu, kisa adli kartta sorun yoktu.

        `min-w-0` tabani kaldirir, `break-words` bosluksuz uzun adi boler,
        `shrink-0` buton grubunu ezdirmez.
      */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="break-words font-medium">{product.name}</h3>
          <p className="break-words text-sm text-gray-500">{product.brand}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
           <TooltipProvider delayDuration={10}>
             <Tooltip>
               <TooltipTrigger asChild>
                 <Button
                   variant="secondaryGray"
                   size="sm"
                   onClick={() => onEditWarrantyDate(product)}
                 >
                   <CalendarIcon className="h-5 w-5" />
                 </Button>
               </TooltipTrigger>
               <TooltipContent>
                 {t(
                   "clientsPage.editWarrantyDate",
                   "Garanti Tarihini Düzenle",
                 )}
               </TooltipContent>
             </Tooltip>
           </TooltipProvider>

           <TooltipProvider delayDuration={10}>
             <Tooltip>
               <TooltipTrigger asChild>
                 <Button
                   variant="secondaryGray"
                   size="sm"
                   onClick={() => onUploadBill(product)}
                 >
                   <FileText className="h-5 w-5" />
                 </Button>
               </TooltipTrigger>
               <TooltipContent>
                 {t("clientsPage.uploadBill", "Fatura Yükle")}
               </TooltipContent>
             </Tooltip>
           </TooltipProvider>
          <TooltipProvider delayDuration={10}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondaryGray"
                  size="sm"
                  onClick={() => onDelete(product)}
                >
                  <TrashIcon className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("clientsPage.deleteProduct", "Ürünü Sil")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="my-4 flex flex-wrap gap-2">
        {product.setupRequired && !product.setupCompleted && (
          <Tag size="sm" variant="yellow">
            {t("clientsPage.setupRequired", "Kurulum Gerekli")}
          </Tag>
        )}
        {product.setupCompleted && (
          <Tag size="sm" variant="green">
            {t("clientsPage.setupCompleted", "Kurulum Yapıldı")}
          </Tag>
        )}
        <Tag size="sm" variant={getSourceInfo(product.warrantySource).color}>
          {getSourceInfo(product.warrantySource).text}
        </Tag>
        <Tag size="sm" variant="blue">
          {t(product.category?.split(" > ").pop())}
        </Tag>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">
            {t("clientsPage.warrantyStatus", "Garanti Durumu")}
          </span>
          <span className="font-medium">{product.remainingWarrantyText}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">
            {t("clientsPage.startDate", "Başlangıç Tarihi")}
          </span>
          <span>{product.warrantyStartDate}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">
            {t("clientsPage.endDate", "Bitiş Tarihi")}
          </span>
          <span>{product.warrantyExpireDate}</span>
        </div>
      </div>

      {(product.serialNumber ||
        product.warrantyFileUrl ||
        product.billingFileUrl ||
        product.serviceFileUrl ||
        product.questionsAndAnswers?.length > 0) && (
        <Accordion type="single" collapsible>
          {[
            {
              id: "documents",
              title: t("clientsPage.documents", "Dökümanlar"),
              show:
                product.warrantyFileUrl ||
                product.billingFileUrl ||
                product.serviceFileUrl,
              content: (
                <div className="space-y-2 pt-2">
                  {[
                    {
                      url: product.warrantyFileUrl,
                      label: t(
                        "clientsPage.warrantyDocument",
                        "Garanti Belgesi",
                      ),
                    },
                    {
                      url: product.billingFileUrl,
                      label: t("clientsPage.bill", "Fatura"),
                    },
                    {
                      url: product.serviceFileUrl,
                      label: t("clientsPage.serviceDocument", "Servis Belgesi"),
                    },
                  ].map(
                    (doc, index) =>
                      doc.url && (
                        <a
                          key={index}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between rounded-lg border p-4 text-sm hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-2">
                            <DocumentIcon />
                            <span>{doc.label}</span>
                          </div>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ),
                  )}
                </div>
              ),
            },
            {
              id: "questionsAndAnswers",
              title: t(
                "clientsPage.questionsAndAnswers",
                "Sorular ve Cevaplar",
              ),
              show: product.questionsAndAnswers?.length > 0,
              content: (
                <div className="space-y-2 pt-2">
                  {product.questionsAndAnswers?.map((qa, index) => (
                    <div key={index} className="rounded-lg border p-4 text-sm">
                      <div className="mb-2">
                        <span className="font-medium">
                          {t("clientsPage.question", "Soru")}:{" "}
                        </span>
                        {qa.question}
                      </div>
                      <div>
                        <span className="font-medium">
                          {t("clientsPage.answer", "Cevap")}:{" "}
                        </span>
                        {qa.answer}
                      </div>
                    </div>
                  ))}
                </div>
              ),
            },
            {
              id: "serialNumber",
              title: t("clientsPage.serialNumber", "Seri Numarası"),
              show: product.serialNumber,
              content: (
                <div className="space-y-2 pt-2">{product.serialNumber}</div>
              ),
            },
          ].map(
            (section) =>
              section.show && (
                <AccordionItem
                  key={section.id}
                  className="mt-4 !border-none"
                  value={section.id}
                >
                  <AccordionTrigger className="text-sm font-medium">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>{section.content}</AccordionContent>
                </AccordionItem>
              ),
          )}
        </Accordion>
      )}
    </div>
  );
};

export default ProductCard;
